import { useQuery } from "@tanstack/react-query";
import { useContainer } from "@src/contexts/container-context";

/**
 * Hook to fetch connections for an organization
 */
export function useConnections(orgCode, options = {}) {
  const { integrationService } = useContainer();

  return useQuery({
    queryKey: ["connections", orgCode],
    queryFn: () => integrationService.getConnectionsList(orgCode),
    enabled: !!orgCode && options.enabled !== false,
    ...options,
  });
}

/**
 * Hook to get a specific connection by ID
 */
export function useConnection(connectionId, orgCode, options = {}) {
  const { data: connections } = useConnections(orgCode, {
    ...options,
    enabled: !!orgCode && !!connectionId && options.enabled !== false,
  });

  return {
    data: connections?.find((conn) => conn.id === connectionId),
    isLoading: options.isLoading,
  };
}

/**
 * Creates a stable query key for connection provider last run queries
 * @param {string} providerName - The provider name (e.g., "clever", "classlink")
 * @param {string[]} orgCodes - Array of organization codes to fetch last run for
 * @returns {Array} React Query key array
 */
export function getConnectionProviderLastRunQueryKey(providerName, orgCodes) {
  const sortedOrgCodes = Array.isArray(orgCodes)
    ? [...orgCodes].sort()
    : [orgCodes];
  return ["connectionProviderLastRun", providerName, sortedOrgCodes];
}

/**
 * Hook to fetch the last run data for a connection provider across multiple organizations
 * @param {string} providerName - The provider name (e.g., "clever", "classlink")
 * @param {string[]} orgCodes - Array of organization codes to fetch last run for
 * @param {Object} options - Additional react-query options
 * @returns {Object} React Query result object
 */
export function useConnectionProviderLastRun(
  providerName,
  orgCodes,
  options = {}
) {
  const { integrationService } = useContainer();

  const queryKey = getConnectionProviderLastRunQueryKey(providerName, orgCodes);

  return useQuery({
    queryKey,
    queryFn: () =>
      integrationService.getConnectionProviderLastRun(providerName, orgCodes),
    enabled:
      !!providerName &&
      Array.isArray(orgCodes) &&
      orgCodes.length > 0 &&
      options.enabled !== false,
    ...options,
  });
}
